/**
 * FE-005 / FE-006: Session Manager (Orchestrator)
 *
 * Ties together the SignalingClient, PeerConnection, and media services
 * into a single state machine that manages the full lifecycle of a
 * DuoCast session: idle -> joining -> waiting -> matched -> connecting -> connected -> ended.
 */

import { createSignal, batch } from "solid-js";
import type {
  JoinAttributes,
  JoinPreferences,
  SessionStage,
  PeerRole,
  MatchedMessage,
  OfferMessage,
  AnswerMessage,
  IceMessage,
  PeerLeftMessage,
  StageChangeMessage,
  RevealAcceptMessage,
  RevealWaitingMessage,
  ErrorMessage,
  RateLimitedMessage,
  BannedMessage,
} from "@duocast/shared";
import { config } from "../lib/config";
import {
  createSignalingClient,
  type SignalingClient,
} from "./signaling-client";
import {
  createPeerConnection,
  type PeerConnection,
  type PeerConnectionConfig,
} from "./peer-connection";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type SessionState =
  | "idle"
  | "joining"
  | "waiting"
  | "matched"
  | "connecting"
  | "connected"
  | "ended";

export interface SessionError {
  code: string;
  message: string;
  details?: Record<string, unknown>;
}

export interface BanInfo {
  expiresAt: string;
  reason: string;
}

export interface RateLimitInfo {
  retryAfterMs: number;
}

// ---------------------------------------------------------------------------
// Session Manager
// ---------------------------------------------------------------------------

export function createSessionManager() {
  // -- Reactive state -------------------------------------------------------
  const [sessionState, setSessionState] = createSignal<SessionState>("idle");
  const [currentStage, setCurrentStage] = createSignal<SessionStage>(1);
  const [remoteStream, setRemoteStream] = createSignal<MediaStream | null>(null);
  const [peerRole, setPeerRole] = createSignal<PeerRole | null>(null);
  const [roomId, setRoomId] = createSignal<string | null>(null);
  const [error, setError] = createSignal<SessionError | null>(null);
  const [banInfo, setBanInfo] = createSignal<BanInfo | null>(null);
  const [rateLimitInfo, setRateLimitInfo] = createSignal<RateLimitInfo | null>(null);
  const [peerLeft, setPeerLeft] = createSignal<boolean>(false);
  const [revealWaiting, setRevealWaiting] = createSignal<boolean>(false);
  const [revealAccepted, setRevealAccepted] = createSignal<boolean>(false);

  // -- Internal state -------------------------------------------------------
  let signaling: SignalingClient | null = null;
  let peer: PeerConnection | null = null;
  let localAudioTrack: MediaStreamTrack | null = null;
  let filteredVideoTrack: MediaStreamTrack | null = null;
  let rawVideoTrack: MediaStreamTrack | null = null;

  // Cleanup functions for signaling handlers
  let cleanupHandlers: (() => void)[] = [];
  // ICE connection timeout
  let iceTimeout: ReturnType<typeof setTimeout> | null = null;

  // -- ICE server config from app config ------------------------------------

  /** Cached ICE servers fetched from Metered (or fallback to STUN-only). */
  let cachedIceServers: RTCIceServer[] | null = null;

  /**
   * Fetch ICE servers from Metered TURN API.
   * Returns STUN+TURN servers with temporary credentials.
   * Falls back to Google STUN if Metered is not configured or fetch fails.
   */
  async function fetchIceServers(): Promise<RTCIceServer[]> {
    // Return cached servers if available
    if (cachedIceServers) {
      console.log('[session] Using cached ICE servers:', cachedIceServers.length, 'servers');
      return cachedIceServers;
    }

    // Try Metered TURN API
    if (config.meteredApiUrl) {
      console.log('[session] Fetching TURN servers from Metered...');
      try {
        const response = await fetch(config.meteredApiUrl);
        if (response.ok) {
          const servers = await response.json() as RTCIceServer[];
          if (Array.isArray(servers) && servers.length > 0) {
            console.log('[session] Got', servers.length, 'ICE servers from Metered (STUN+TURN)');
            cachedIceServers = servers;
            return servers;
          }
        } else {
          console.warn('[session] Metered API returned', response.status);
        }
      } catch (err) {
        console.warn('[session] Failed to fetch Metered TURN servers, using STUN fallback:', err);
      }
    } else {
      console.warn('[session] No VITE_METERED_API_URL configured — STUN only, TURN unavailable. ~20-40% of connections will fail.');
    }

    // Fallback: STUN-only (works for ~60-80% of connections)
    const fallback: RTCIceServer[] = [];
    if (config.stunUrls.length > 0) {
      fallback.push({ urls: config.stunUrls as unknown as string[] });
    }
    console.log('[session] Using STUN-only fallback:', fallback);
    cachedIceServers = fallback;
    return fallback;
  }

  // -- Signaling event wiring -----------------------------------------------

  function wireSignalingHandlers(): void {
    if (!signaling) return;

    cleanupHandlers.push(
      signaling.on<MatchedMessage>("MATCHED", handleMatched),
      signaling.on<OfferMessage>("OFFER", handleOffer),
      signaling.on<AnswerMessage>("ANSWER", handleAnswer),
      signaling.on<IceMessage>("ICE", handleIce),
      signaling.on<PeerLeftMessage>("PEER_LEFT", handlePeerLeft),
      signaling.on<StageChangeMessage>("STAGE_CHANGE", handleStageChange),
      signaling.on<RevealAcceptMessage>("REVEAL_ACCEPT", handleRevealAccept),
      signaling.on<RevealWaitingMessage>("REVEAL_WAITING", handleRevealWaiting),
      signaling.on<ErrorMessage>("ERROR", handleError),
      signaling.on<RateLimitedMessage>("RATE_LIMITED", handleRateLimited),
      signaling.on<BannedMessage>("BANNED", handleBanned),
    );
  }

  function unwireSignalingHandlers(): void {
    cleanupHandlers.forEach((unsub) => unsub());
    cleanupHandlers = [];
  }

  // -- Peer connection setup ------------------------------------------------

  function setupPeerConnection(iceServers: RTCIceServer[]): PeerConnection {
    const pcConfig: PeerConnectionConfig = {
      iceServers,
      stripHostCandidates: false,
    };

    const peerConn = createPeerConnection(pcConfig);
    peerConn.createConnection();

    // Wire ICE candidates to signaling
    peerConn.onIceCandidate((candidate) => {
      const currentRoomId = roomId();
      if (signaling && currentRoomId) {
        signaling.sendIce(currentRoomId, {
          candidate: candidate.candidate,
          sdpMid: candidate.sdpMid ?? null,
          sdpMLineIndex: candidate.sdpMLineIndex ?? null,
        });
      }
    });

    // Wire remote stream from peer connection to session state
    peerConn.onRemoteStream((stream) => {
      setRemoteStream(stream);
    });

    // Wire connection state — transition to "connected" when WebRTC connects
    peerConn.onStateChange((pcState) => {
      if (pcState === 'connected') {
        // Clear ICE timeout — we're connected!
        if (iceTimeout) {
          clearTimeout(iceTimeout);
          iceTimeout = null;
        }
        setSessionState('connected');
      } else if (pcState === 'failed') {
        if (iceTimeout) {
          clearTimeout(iceTimeout);
          iceTimeout = null;
        }
        setError({ code: 'PEER_CONNECTION_FAILED', message: 'WebRTC connection failed. This may be a network issue — try switching between WiFi and mobile data.' });
        setSessionState('ended');
      }
    });

    // ICE connection timeout: if not connected within 15s, show error
    iceTimeout = setTimeout(() => {
      if (sessionState() === 'connecting' || sessionState() === 'matched') {
        console.warn('[session] ICE connection timeout after 15s');
        setError({
          code: 'PEER_CONNECTION_FAILED',
          message: 'Could not establish a peer-to-peer connection. This is usually a network issue. Try a different network or disable VPN.',
        });
        setSessionState('ended');
        closePeerConnection();
      }
    }, 15_000);

    // Add local audio track if available
    if (localAudioTrack) {
      peerConn.addLocalAudioTrack(localAudioTrack);
    }

    // Add local video track immediately so the remote peer receives video
    // from the start (DuoCast podcast mode — no stage gating).
    const initialVideoTrack = filteredVideoTrack ?? rawVideoTrack;
    if (initialVideoTrack) {
      peerConn.replaceVideoTrack(initialVideoTrack).catch(() => {});
    }

    return peerConn;
  }

  // -- Signaling message handlers -------------------------------------------

  function handleMatched(msg: MatchedMessage): void {
    batch(() => {
      setSessionState("matched");
      setRoomId(msg.roomId);
      setPeerRole(msg.role);
      setCurrentStage(1);
      setPeerLeft(false);
      setRevealWaiting(false);
      setRevealAccepted(false);
      setError(null);
    });

    // Fetch ICE servers (cached after first call), then create peer connection
    fetchIceServers()
      .then((iceServers) => {
        peer = setupPeerConnection(iceServers);

        if (msg.role === "initiator") {
          // Initiator creates and sends the offer
          setSessionState("connecting");
          peer
            .createOffer()
            .then((offer) => {
              signaling?.sendOffer(msg.roomId, offer.sdp!);
            })
            .catch((err) => {
              console.error("[session] Failed to create offer:", err);
              setError({ code: "OFFER_FAILED", message: "Failed to create offer" });
            });
        }
        // Responder waits for the offer via handleOffer
      })
      .catch((err) => {
        console.error("[session] Failed to fetch ICE servers:", err);
        setError({ code: "ICE_FETCH_FAILED", message: "Failed to configure connection" });
      });
  }

  function handleOffer(msg: OfferMessage): void {
    if (!peer || msg.roomId !== roomId()) return;

    setSessionState("connecting");
    peer
      .handleOffer({ type: "offer", sdp: msg.sdp })
      .then((answer) => {
        signaling?.sendAnswer(msg.roomId, answer.sdp!);
      })
      .catch((err) => {
        console.error("[session] Failed to handle offer:", err);
        setError({ code: "ANSWER_FAILED", message: "Failed to create answer" });
      });
  }

  function handleAnswer(msg: AnswerMessage): void {
    if (!peer || msg.roomId !== roomId()) return;

    peer
      .handleAnswer({ type: "answer", sdp: msg.sdp })
      .then(() => {
        // Don't set "connected" here — wait for onStateChange('connected')
        // from the actual WebRTC connection state
        console.log('[session] Remote description set, waiting for ICE to connect...');
      })
      .catch((err) => {
        console.error("[session] Failed to handle answer:", err);
        setError({
          code: "ANSWER_HANDLE_FAILED",
          message: "Failed to set remote description",
        });
      });
  }

  function handleIce(msg: IceMessage): void {
    if (!peer || msg.roomId !== roomId()) return;

    peer.addIceCandidate({
      candidate: msg.candidate.candidate,
      sdpMid: msg.candidate.sdpMid ?? undefined,
      sdpMLineIndex: msg.candidate.sdpMLineIndex ?? undefined,
    }).catch((err) => {
      console.warn("[session] Failed to add ICE candidate:", err);
    });
  }

  function handlePeerLeft(msg: PeerLeftMessage): void {
    if (msg.roomId !== roomId()) return;

    closePeerConnection();
    batch(() => {
      setPeerLeft(true);
      setSessionState("ended");
    });
  }

  function handleStageChange(msg: StageChangeMessage): void {
    if (msg.roomId !== roomId()) return;

    setCurrentStage(msg.stage);

    // Handle video track transitions
    if (peer) {
      if (msg.stage === 2 && filteredVideoTrack) {
        // Stage 2: add filtered video
        peer.replaceVideoTrack(filteredVideoTrack).catch((err) => {
          console.warn("[session] Failed to set filtered video track:", err);
        });
      } else if (msg.stage === 3 && rawVideoTrack) {
        // Stage 3: switch to raw video
        peer.replaceVideoTrack(rawVideoTrack).catch((err) => {
          console.warn("[session] Failed to set raw video track:", err);
        });
      }
    }
  }

  function handleRevealAccept(msg: RevealAcceptMessage): void {
    if (msg.roomId !== roomId()) return;

    setRevealAccepted(true);

    // Switch to raw video track on mutual reveal
    if (peer && rawVideoTrack) {
      peer.replaceVideoTrack(rawVideoTrack).catch((err) => {
        console.warn("[session] Failed to set raw video on reveal:", err);
      });
    }
  }

  function handleRevealWaiting(msg: RevealWaitingMessage): void {
    if (msg.roomId !== roomId()) return;

    setRevealWaiting(true);
  }

  function handleError(msg: ErrorMessage): void {
    setError({
      code: msg.code,
      message: msg.message,
      details: msg.details,
    });
  }

  function handleRateLimited(msg: RateLimitedMessage): void {
    batch(() => {
      setRateLimitInfo({ retryAfterMs: msg.retryAfterMs });
      setSessionState("ended");
    });
  }

  function handleBanned(msg: BannedMessage): void {
    batch(() => {
      setBanInfo({ expiresAt: msg.expiresAt, reason: msg.reason });
      setSessionState("ended");
    });
    // Server will close the connection after BANNED
  }

  // -- Peer connection teardown ---------------------------------------------

  function closePeerConnection(): void {
    if (iceTimeout) {
      clearTimeout(iceTimeout);
      iceTimeout = null;
    }
    if (peer) {
      peer.close();
      peer = null;
    }
    setRemoteStream(null);
  }

  // -- Public API -----------------------------------------------------------

  /**
   * Start a new session: connect to signaling, join the queue.
   */
  function startSession(
    attributes: JoinAttributes,
    preferences?: JoinPreferences,
  ): void {
    if (sessionState() !== "idle" && sessionState() !== "ended") {
      return;
    }

    batch(() => {
      setSessionState("joining");
      setError(null);
      setBanInfo(null);
      setRateLimitInfo(null);
      setPeerLeft(false);
    });

    // Pre-fetch ICE servers so they're cached by the time a match happens
    fetchIceServers().catch(() => {});

    // Create signaling client and connect
    signaling = createSignalingClient();
    wireSignalingHandlers();

    signaling.connect(config.signalingUrl);

    // Once connected, send JOIN
    // We watch the connection state and join when connected
    const checkConnection = setInterval(() => {
      if (signaling && signaling.connectionState() === "connected") {
        clearInterval(checkConnection);
        signaling.join(attributes, preferences);
        setSessionState("waiting");
      } else if (
        signaling &&
        (signaling.connectionState() === "error" ||
          signaling.connectionState() === "disconnected")
      ) {
        clearInterval(checkConnection);
        setError({
          code: "CONNECTION_FAILED",
          message: "Failed to connect to signaling server",
        });
        setSessionState("ended");
      }
    }, 100);

    // Safety timeout: don't poll forever
    setTimeout(() => clearInterval(checkConnection), 30_000);
  }

  /**
   * Skip current peer and re-enter matching queue.
   */
  function next(): void {
    const currentRoomId = roomId();
    if (!currentRoomId || !signaling) return;

    // Send NEXT to signaling
    signaling.next(currentRoomId);

    // Close current peer connection
    closePeerConnection();

    // Reset state for next match
    batch(() => {
      setSessionState("waiting");
      setRoomId(null);
      setPeerRole(null);
      setCurrentStage(1);
      setPeerLeft(false);
      setRevealWaiting(false);
      setRevealAccepted(false);
      setError(null);
    });
  }

  /**
   * End the current session and return to idle.
   */
  function endSession(): void {
    const currentRoomId = roomId();

    // Send NEXT to leave room if we're in one
    if (signaling && currentRoomId) {
      signaling.next(currentRoomId);
    }

    closePeerConnection();

    // Disconnect signaling
    if (signaling) {
      unwireSignalingHandlers();
      signaling.disconnect();
      signaling = null;
    }

    // Reset all state
    batch(() => {
      setSessionState("idle");
      setRoomId(null);
      setPeerRole(null);
      setCurrentStage(1);
      setRemoteStream(null);
      setError(null);
      setBanInfo(null);
      setRateLimitInfo(null);
      setPeerLeft(false);
      setRevealWaiting(false);
      setRevealAccepted(false);
    });

  }

  /**
   * Set the local audio track for the peer connection.
   * Should be called before startSession or during the session.
   */
  function setLocalAudioTrack(track: MediaStreamTrack | null): void {
    localAudioTrack = track;
  }

  /**
   * Set the filtered video track for Stage 2.
   */
  function setFilteredVideoTrack(track: MediaStreamTrack | null): void {
    filteredVideoTrack = track;
  }

  /**
   * Set the raw video track for Stage 3 / reveal.
   */
  function setRawVideoTrack(track: MediaStreamTrack | null): void {
    rawVideoTrack = track;
  }

  /**
   * Request identity reveal (Stage 3 opt-in).
   */
  function revealRequest(): void {
    const currentRoomId = roomId();
    if (signaling && currentRoomId) {
      signaling.revealRequest(currentRoomId);
    }
  }

  /**
   * Report the current peer.
   */
  function report(category: "inappropriate" | "harassment" | "spam" | "underage"): void {
    const currentRoomId = roomId();
    if (signaling && currentRoomId) {
      signaling.report(currentRoomId, category);
    }
  }

  // -- Expose for reactive UI -----------------------------------------------

  return {
    // Reactive state
    sessionState,
    currentStage,
    remoteStream,
    peerRole,
    roomId,
    error,
    banInfo,
    rateLimitInfo,
    peerLeft,
    revealWaiting,
    revealAccepted,

    // Online count (from signaling)
    get onlineCount() {
      return signaling?.onlineCount ?? (() => 0);
    },

    // Session lifecycle
    startSession,
    next,
    endSession,

    // Media track setters
    setLocalAudioTrack,
    setFilteredVideoTrack,
    setRawVideoTrack,

    // Actions
    revealRequest,
    report,

    // Internal (for testing)
    get _signaling() {
      return signaling;
    },
    get _peer() {
      return peer;
    },
  };
}

export type SessionManager = ReturnType<typeof createSessionManager>;
